import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CircleDot, Layers, Scissors } from 'lucide-react';
import { pressingProgress } from '../utils/pressing';
import {
  isOnFloor, needsCutting, needsBoring, needsPressing,
  cuttingStage, boringStage, STAGE_LABEL, STAGE_CHIP,
} from '../utils/stages';

// What's pending at each stage, across the job cards still on the floor.
// Shared by supervisor, owner and coordinator. Read-only: tap a card to open it.
//   Cutting  - status only (planned and actual cutting still share one field)
//   Pressing - exact sheets left, line by line
//   Boring   - status, "Finished" from the boring operator's Finish button

const PREVIEW = 3;

const byOldest = (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0);
const name = (jc) => jc.customer_name || jc.client_name || '';

const StageCounts = ({ counts }) => (
  <div className="flex flex-wrap gap-1.5">
    {['not_started', 'started', 'completed'].map((k) => (
      <span key={k} className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STAGE_CHIP[k]}`}>
        {STAGE_LABEL[k]} {counts[k] || 0}
      </span>
    ))}
  </div>
);

const Column = ({ icon, title, summary, rows, renderStatus, note, onOpenCard, testid }) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? rows : rows.slice(0, PREVIEW);
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-col gap-2 min-w-0" data-testid={testid}>
      <div className="flex items-center gap-2 font-semibold text-gray-900">{icon}{title}</div>
      {summary}
      {rows.length === 0 ? (
        <p className="text-sm text-green-700 py-1">Nothing pending ✓</p>
      ) : (
        <div className="flex flex-col">
          {visible.map((jc) => (
            <button
              type="button"
              key={jc.id}
              onClick={() => onOpenCard && onOpenCard(jc)}
              className={`flex items-center justify-between gap-2 py-1.5 border-t border-gray-100 text-left text-sm ${onOpenCard ? 'hover:bg-gray-50' : 'cursor-default'}`}
            >
              <span className="min-w-0 truncate">
                <span className="font-semibold text-blue-700">{jc.job_card_number}</span>{' '}
                <span className="text-gray-700">{name(jc)}</span>
              </span>
              <span className="shrink-0">{renderStatus(jc)}</span>
            </button>
          ))}
          {rows.length > PREVIEW && (
            <button type="button" className="text-xs text-blue-600 hover:underline text-left pt-1" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Show less' : `See all ${rows.length} pending ›`}
            </button>
          )}
        </div>
      )}
      <p className="text-[11px] text-gray-500 border-t border-dashed border-gray-200 pt-1.5 mt-auto">{note}</p>
    </div>
  );
};

const FloorStatus = ({ jobCards, onOpenCard }) => {
  const data = useMemo(() => {
    const floor = (jobCards || []).filter(isOnFloor);

    const cutCards = floor.filter(needsCutting);
    const cutCounts = {};
    cutCards.forEach((jc) => { const s = cuttingStage(jc); cutCounts[s] = (cutCounts[s] || 0) + 1; });
    const cutPending = cutCards
      .filter((jc) => cuttingStage(jc) !== 'completed')
      .sort((a, b) => (cuttingStage(a) === 'started' ? 0 : 1) - (cuttingStage(b) === 'started' ? 0 : 1) || byOldest(a, b));

    const pressPending = floor
      .filter(needsPressing)
      .map((jc) => ({ jc, p: pressingProgress(jc) }))
      .filter(({ p }) => p.balance > 0)
      .sort((a, b) => b.p.balance - a.p.balance);
    const sheetsLeft = pressPending.reduce((s, { p }) => s + p.balance, 0);

    const boreCards = floor.filter(needsBoring);
    const boreCounts = {};
    boreCards.forEach((jc) => { const s = boringStage(jc); boreCounts[s] = (boreCounts[s] || 0) + 1; });
    const borePending = boreCards
      .filter((jc) => boringStage(jc) !== 'completed')
      .sort((a, b) => (boringStage(a) === 'started' ? 0 : 1) - (boringStage(b) === 'started' ? 0 : 1) || byOldest(a, b));

    return { total: floor.length, cutCounts, cutPending, pressPending, sheetsLeft, boreCounts, borePending };
  }, [jobCards]);

  const stageChip = (stage) => (
    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STAGE_CHIP[stage]}`}>{STAGE_LABEL[stage]}</span>
  );
  const pressById = Object.fromEntries(data.pressPending.map(({ jc, p }) => [jc.id, p]));

  return (
    <Card className="border-amber-200 bg-amber-50/40" data-testid="floor-status">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between gap-2 flex-wrap">
          <span>Floor status: what's pending at each stage</span>
          <span className="text-xs font-normal text-gray-500">{data.total} job card{data.total === 1 ? '' : 's'} on the floor</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Column
            testid="floor-cutting"
            icon={<Scissors className="w-4 h-4 text-orange-600" />}
            title="Cutting"
            summary={<StageCounts counts={data.cutCounts} />}
            rows={data.cutPending}
            renderStatus={(jc) => stageChip(cuttingStage(jc))}
            note="Status from the cutting operator's Finish button. Quantities left will come later."
            onOpenCard={onOpenCard}
          />
          <Column
            testid="floor-pressing"
            icon={<Layers className="w-4 h-4 text-amber-600" />}
            title="Pressing"
            summary={
              <p className="text-sm text-gray-700">
                <strong className="text-lg text-gray-900">{data.sheetsLeft}</strong> sheets left on{' '}
                <strong className="text-lg text-gray-900">{data.pressPending.length}</strong> card{data.pressPending.length === 1 ? '' : 's'}
              </p>
            }
            rows={data.pressPending.map(({ jc }) => jc)}
            renderStatus={(jc) => {
              const p = pressById[jc.id];
              return <span className="text-xs text-gray-700 tabular-nums">{p.done}/{p.planned} · <strong>{p.balance} left</strong></span>;
            }}
            note="Exact: counted line by line from the pressing operators' entries."
            onOpenCard={onOpenCard}
          />
          <Column
            testid="floor-boring"
            icon={<CircleDot className="w-4 h-4 text-purple-600" />}
            title="Minifix / Boring"
            summary={<StageCounts counts={data.boreCounts} />}
            rows={data.borePending}
            renderStatus={(jc) => stageChip(boringStage(jc))}
            note="Finished when the boring operator taps Finish. Cards with entries from before show as In progress."
            onOpenCard={onOpenCard}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default FloorStatus;
